import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { 
  createClientQueryThunk, 
  updateClientQueryThunk, 
  fetchClientQueryByIdThunk 
} from "../features/queries/queryThunks";
import { fetchStoresThunk } from "../features/stores/storeThunks";
import { fetchEmployeesThunk } from "../features/employees/employeeThunks";

function CreateEditClientQueryForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { queryId } = useParams();
  
  // Are we editing or creating?
  const editMode = Boolean(queryId);

  // Redux states
  const { queries, loading: queriesLoading } = useSelector((state) => state.queries);
  const { stores, loading: storesLoading } = useSelector((state) => state.stores);
  const { employees, loading: employeesLoading } = useSelector((state) => state.employees);
  const { user } = useSelector((state) => state.auth);

  // We assume user.employee_profile.id is the employee ID
  const employeeId = user?.employee_profile?.id;
  const storeOwnerId = user?.store_owner_profile?.id;

  // Find existing query if editing
  const existingQuery = editMode ? queries.find((q) => q.id === parseInt(queryId)) : null;

  // Initial form state
  const initialFormData = {
    query_type: "incident",
    subject: "",
    details: "",
    status: "",
    incident_date: "",
    incident_time: "",
    task_assigned: "",
    store: "",
    employee: "",
  };

  // Local form state
  const [formData, setFormData] = useState(initialFormData);
  const [newFiles, setNewFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);

  // Modal state
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
    confirmAction: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  // Status options
  const statusOptions = [
    { value: "", label: "Select Status" },
    { value: "pending", label: "Pending" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  // Modal functions
  const showModal = (type, title, message, confirmAction = null, confirmText = 'Confirm', cancelText = 'Cancel') => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      confirmAction,
      confirmText,
      cancelText
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleModalConfirm = () => {
    if (modalState.confirmAction) {
      modalState.confirmAction();
    }
    closeModal();
  };

  // Navigation handlers
  const handleCancel = () => {
    navigate('/clientqueriestable');
  };

  const handleSuccess = () => {
    navigate('/clientqueriestable');
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status) => {
    if (!status) return 'client-query-status-default';
    const statusLower = status.toLowerCase().replace(/\s+/g, '-');
    return `client-query-status-${statusLower}`;
  };

  // Helper function to capitalize text
  const capitalizeText = (text) => {
    if (!text) return '';
    return text.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchStoresThunk());
    dispatch(fetchEmployeesThunk());
  }, [dispatch]);

  // Fetch individual query if editing and not in store
  useEffect(() => {
    if (editMode && queryId && !existingQuery) {
      dispatch(fetchClientQueryByIdThunk(queryId));
    }
  }, [editMode, queryId, existingQuery, dispatch]);

  // Populate form with existing data if editing
  useEffect(() => {
    if (editMode && existingQuery) {
      setFormData({
        query_type: existingQuery.query_type || "incident",
        subject: existingQuery.subject || "",
        details: existingQuery.details || "",
        status: existingQuery.status || "",
        incident_date: existingQuery.incident_date || "",
        incident_time: existingQuery.incident_time || "",
        task_assigned: existingQuery.task_assigned || "",
        store: existingQuery.store?.id || "",
        employee: existingQuery.employee || "",
      });

      // Set existing media files
      if (existingQuery.media && existingQuery.media.length > 0) {
        setExistingFiles(existingQuery.media);
      }
      setFilesToDelete([]);
    }
  }, [editMode, existingQuery]);

  // Handler for form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // File handling for new uploads
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setNewFiles((prev) => [...prev, ...selectedFiles]);
  };

  // Delete new file
  const handleNewFileDelete = (index) => {
    setNewFiles((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  // Delete existing file
  const handleExistingFileDelete = (fileId) => {
    setFilesToDelete(prev => {
      if (!prev.includes(fileId)) {
        return [...prev, fileId];
      }
      return prev;
    });
    
    setExistingFiles(prev => prev.filter(file => file.id !== fileId));
  };

  // Reset form
  const handleReset = () => {
    showModal(
      'confirm',
      'Reset Form',
      'Are you sure you want to reset the form? All unsaved changes will be lost.',
      () => {
        setFormData(initialFormData);
        setNewFiles([]);
        if (editMode && existingQuery) {
          setExistingFiles(existingQuery.media || []);
          setFilesToDelete([]);
        } else {
          setExistingFiles([]);
          setFilesToDelete([]);
        }
      },
      'Reset',
      'Cancel'
    );
  };

  // Handle form submission
  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    // Validation
    if (!formData.subject.trim()) {
      showModal('error', 'Validation Error', 'Subject is required.');
      return;
    }

    if (!formData.store) {
      showModal('error', 'Validation Error', 'Please select a store.');
      return;
    }

    if (formData.query_type === "incident" && !formData.incident_date) {
      showModal('error', 'Validation Error', 'Incident date is required for incident queries.');
      return;
    }

    if (editMode) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  // CREATE
  const handleCreate = () => {
    const formDataToSend = new FormData();
    
    // Add form fields
    // NOTE: backend serializer uses store_id / employee_id as write-only fields
    formDataToSend.append('store_id', formData.store);
    if (formData.employee) {
      formDataToSend.append('employee_id', formData.employee);
    }
    formDataToSend.append('query_type', formData.query_type);
    formDataToSend.append('subject', formData.subject);
    formDataToSend.append('details', formData.details || '');
    formDataToSend.append('status', formData.status || '');
    
    if (formData.query_type === "incident") {
      formDataToSend.append('incident_date', formData.incident_date);
      formDataToSend.append('incident_time', formData.incident_time || '');
    } else if (formData.query_type === "assign_task") {
      formDataToSend.append('task_assigned', formData.task_assigned || '');
    }

    // Add new files
    newFiles.forEach((file) => {
      formDataToSend.append(`media_files`, file);
    });

    dispatch(createClientQueryThunk(formDataToSend))
      .unwrap()
      .then(() => {
        showModal(
          'success',
          'Success!',
          'Client query created successfully!',
          handleSuccess,
          'Continue'
        );
      })
      .catch((err) => {
        console.error("Failed to create client query:", err);
        showModal('error', 'Error', 'Failed to create client query. Please try again.');
      });
  };

  // UPDATE
  const handleUpdate = () => {
    if (!existingQuery) return;

    showModal(
      'confirm',
      'Confirm Update',
      'Are you sure you want to update this client query?',
      () => {
        const formDataToSend = new FormData();
        
        // Add form fields
        // NOTE: backend serializer uses store_id / employee_id as write-only fields
        formDataToSend.append('store_id', formData.store);
        if (formData.employee) {
          formDataToSend.append('employee_id', formData.employee);
        }
        formDataToSend.append('query_type', formData.query_type);
        formDataToSend.append('subject', formData.subject);
        formDataToSend.append('details', formData.details || '');
        formDataToSend.append('status', formData.status || '');
        
        if (formData.query_type === "incident") {
          formDataToSend.append('incident_date', formData.incident_date);
          formDataToSend.append('incident_time', formData.incident_time || '');
        } else if (formData.query_type === "assign_task") {
          formDataToSend.append('task_assigned', formData.task_assigned || '');
        }

        // Add new files
        newFiles.forEach((file) => {
          formDataToSend.append(`media_files`, file);
        });

        // Handle file deletions
        if (filesToDelete.length > 0) {
          formDataToSend.append('files_to_delete', JSON.stringify(filesToDelete));
        }

        const payload = {
          queryId: existingQuery.id,
          updates: formDataToSend,
        };

        dispatch(updateClientQueryThunk(payload))
          .unwrap()
          .then(() => {
            setFilesToDelete([]);
            showModal(
              'success',
              'Success!',
              'Client query updated successfully!',
              handleSuccess,
              'Continue'
            );
          })
          .catch((err) => {
            console.error("Failed to update client query:", err);
            showModal('error', 'Error', 'Failed to update client query. Please try again.');
          });
      },
      'Update',
      'Cancel'
    );
  };

  // Helper function to get file name from URL
  const getFileNameFromUrl = (url) => {
    return url.split('/').pop().split('?')[0];
  };

  // Loading state while fetching individual query
  if (queriesLoading && editMode && !existingQuery) {
    return (
      <div className="client-query-content">
        <div className="client-query-layout">
          <div className="client-query-image-side">
            <div className="client-query-image-content">
              {/* Content at top if needed */}
            </div>
            <button 
              type="button" 
              className="client-query-back-btn"
              onClick={handleCancel}
            >
              ← Back
            </button>
          </div>
          
         
        </div>
      </div>
    );
  }

  return (
    <div className="client-query-content">
      {/* Modal */}
      {modalState.isOpen && (
        <div className="client-query-modal-overlay">
          <div className="client-query-modal">
            <div className="client-query-modal-header">
              <div className={`client-query-modal-icon client-query-modal-icon-${modalState.type}`}>
                {modalState.type === 'success' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {modalState.type === 'error' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {modalState.type === 'confirm' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.228 9C8.337 7.8 9.22 7 10.5 7H13.5C14.78 7 15.663 7.8 15.772 9M8.228 9L8.228 9C8.228 9 8.228 9 8.228 9ZM8.228 9C8.337 10.2 9.22 11 10.5 11H13.5C14.78 11 15.663 10.2 15.772 9M15.772 9C15.772 9 15.772 9 15.772 9V9ZM15.772 9L15.772 9ZM7 9H17M10 3V7M14 3V7M7 9V20C7 20.5523 7.44772 21 8 21H16C16.5523 21 17 20.5523 17 20V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <h3 className="client-query-modal-title">{modalState.title}</h3>
            </div>
            
            <div className="client-query-modal-body">
              <p className="client-query-modal-message">{modalState.message}</p>
            </div>
            
            <div className="client-query-modal-footer">
              {modalState.type === 'confirm' && (
                <button 
                  className="client-query-modal-btn client-query-modal-btn-secondary"
                  onClick={closeModal}
                >
                  {modalState.cancelText}
                </button>
              )}
              <button 
                className={`client-query-modal-btn ${
                  modalState.type === 'error' 
                    ? 'client-query-modal-btn-danger' 
                    : 'client-query-modal-btn-primary'
                }`}
                onClick={modalState.type === 'confirm' ? handleModalConfirm : closeModal}
              >
                {modalState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="client-query-layout">
        {/* Left side - Image */}
        <div className="client-query-image-side">
          <div className="client-query-image-content">
            {/* Content at top if needed */}
          </div>
          {/* Back button at bottom of left panel */}
          <button 
            type="button" 
            className="client-query-back-btn"
            onClick={handleCancel}
          >
            ← Back
          </button>
        </div>
        
        {/* Right side - Form */}
        <div className="client-query-form-side">
          <div className="client-query-container">
            <div className="client-query-header">
              <h1>{editMode ? "Edit Client Query" : "Create Client Query"}</h1>
              {editMode && formData.status && (
                <span className={`client-query-status-badge ${getStatusBadgeClass(formData.status)}`}>
                  {capitalizeText(formData.status)}
                </span>
              )}
            </div>

            <div className="client-query-form">
              <div className="client-query-section">
                <h2>Query Information</h2>
                
                <div className="client-query-group">
                  <label htmlFor="query_type">Query Type</label>
                  <div className="client-query-radio-options">
                    <label className="client-query-radio-option">
                      <input
                        type="radio"
                        name="query_type"
                        value="incident"
                        checked={formData.query_type === "incident"}
                        onChange={handleChange}
                        required
                      />
                      <div className="client-query-radio-content">
                        <div className="client-query-radio-check"></div>
                        <div>
                          <span className="client-query-radio-title">Incident Report</span>
                          <p className="client-query-radio-description">Report an incident that occurred at a store</p>
                        </div>
                      </div>
                    </label>
                    
                    <label className="client-query-radio-option">
                      <input
                        type="radio"
                        name="query_type"
                        value="assign_task"
                        checked={formData.query_type === "assign_task"}
                        onChange={handleChange}
                      />
                      <div className="client-query-radio-content">
                        <div className="client-query-radio-check"></div>
                        <div>
                          <span className="client-query-radio-title">Assign Task</span>
                          <p className="client-query-radio-description">Assign a task to an employee</p>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="client-query-group">
                  <label htmlFor="store">Store<span className="required">*</span></label>
                  <div className="client-query-select-wrapper">
                    <select
                      id="store"
                      name="store"
                      className="client-query-select"
                      value={formData.store}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select store</option>
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.store_name} - {store.store_city}
                        </option>
                      ))}
                    </select>
                  </div>
                  {storesLoading && <span className="client-query-loading-text">Loading stores...</span>}
                </div>

                <div className="client-query-group">
                  <label htmlFor="employee">Assign to Employee</label>
                  <div className="client-query-select-wrapper">
                    <select
                      id="employee"
                      name="employee"
                      className="client-query-select"
                      value={formData.employee}
                      onChange={handleChange}
                    >
                      <option value="">Select employee (optional)</option>
                      {employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.user?.first_name} {employee.user?.last_name} - {employee.role}
                        </option>
                      ))}
                    </select>
                  </div>
                  {employeesLoading && <span className="client-query-loading-text">Loading employees...</span>}
                </div>

                <div className="client-query-group">
                  <label htmlFor="status">Status</label>
                  <div className="client-query-select-wrapper">
                    <select
                      id="status"
                      name="status"
                      className="client-query-select"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="client-query-group">
                  <label htmlFor="subject">Subject<span className="required">*</span></label>
                  <input
                    id="subject"
                    type="text"
                    name="subject"
                    className="client-query-input"
                    placeholder="Enter query subject..."
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="client-query-group">
                  <label htmlFor="details">Details</label>
                  <textarea
                    id="details"
                    name="details"
                    className="client-query-textarea"
                    placeholder="Provide detailed description..."
                    value={formData.details}
                    onChange={handleChange}
                    rows="4"
                  />
                </div>
              </div>

              {/* Incident-specific fields */}
              {formData.query_type === "incident" && (
                <div className="client-query-section">
                  <h2>Incident Details</h2>
                  
                  <div className="client-query-group">
                    <label htmlFor="incident_date">Incident Date<span className="required">*</span></label>
                    <input
                      id="incident_date"
                      type="date"
                      name="incident_date"
                      className="client-query-date"
                      value={formData.incident_date}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="client-query-group">
                    <label htmlFor="incident_time">Incident Time</label>
                    <input
                      id="incident_time"
                      type="time"
                      name="incident_time"
                      className="client-query-time"
                      value={formData.incident_time}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              )}

              {/* Task assignment fields */}
              {formData.query_type === "assign_task" && (
                <div className="client-query-section">
                  <h2>Task Details</h2>
                  
                  <div className="client-query-group">
                    <label htmlFor="task_assigned">Task Description</label>
                    <textarea
                      id="task_assigned"
                      name="task_assigned"
                      className="client-query-textarea"
                      placeholder="Describe the task to be assigned..."
                      value={formData.task_assigned}
                      onChange={handleChange}
                      rows="3"
                    />
                  </div>
                </div>
              )}

              <div className="client-query-section">
                <h2>Attachments</h2>
                
                {/* Display existing files from server */}
                {existingFiles.length > 0 && (
                  <div className="client-query-file-list">
                    <h3>Existing Files ({existingFiles.length})</h3>
                    <ul>
                      {existingFiles.map((file) => (
                        <li key={`existing-${file.id}`} className="client-query-file-item">
                          <a 
                            href={file.media} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="client-query-file-link"
                          >
                            <div className="client-query-file-icon">📎</div>
                            <span className="client-query-file-name">
                              {getFileNameFromUrl(file.media)}
                            </span>
                          </a>
                          <button
                            type="button"
                            className="client-query-delete-file"
                            onClick={() => handleExistingFileDelete(file.id)}
                            aria-label="Delete file"
                          >
                            &times;
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* File upload for new files */}
                <div className="client-query-file-container">
                  <label className="client-query-file-upload">
                    <input 
                      type="file" 
                      multiple 
                      onChange={handleFileChange}
                      className="client-query-file-input"
                    />
                    <div className="client-query-upload-content">
                      <div className="client-query-upload-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                      </div>
                      <div className="client-query-upload-text">
                        <span>Upload {existingFiles.length > 0 ? 'additional ' : ''}files</span>
                        <p>Click to browse or drag files here</p>
                      </div>
                    </div>
                  </label>
                </div>

                {/* Display newly uploaded files */}
                {newFiles.length > 0 && (
                  <div className="client-query-file-list">
                    <h3>New Files ({newFiles.length})</h3>
                    <ul>
                      {newFiles.map((file, index) => (
                        <li key={`new-${index}`} className="client-query-file-item">
                          <div className="client-query-file-link">
                            <div className="client-query-file-icon">📎</div>
                            <span className="client-query-file-name">{file.name}</span>
                          </div>
                          <button
                            type="button"
                            className="client-query-delete-file"
                            onClick={() => handleNewFileDelete(index)}
                            aria-label="Delete file"
                          >
                            &times;
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="client-query-actions">
                <button 
                  type="button" 
                  className="client-query-btn-secondary"
                  onClick={handleReset}
                  disabled={queriesLoading}
                >
                  Reset Form
                </button>
                <div className="client-query-right-actions">
                  <button 
                    type="button" 
                    className="client-query-btn-outline"
                    onClick={handleCancel}
                    disabled={queriesLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="client-query-btn-primary"
                    onClick={handleSubmit}
                    disabled={queriesLoading}
                  >
                    {queriesLoading ? 'Processing...' : editMode ? 'Update Query' : 'Create Query'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .client-query-content {
          background: #fafbfc;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .client-query-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }

        .client-query-loader {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid#f43f5e;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .client-query-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }

        .client-query-modal {
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          max-width: 400px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .client-query-modal-header {
          padding: 24px 24px 16px 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .client-query-modal-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .client-query-modal-icon-success {
          background: #d1fae5;
          color: #065f46;
        }

        .client-query-modal-icon-error {
          background: #fee2e2;
          color: #991b1b;
        }

        .client-query-modal-icon-confirm {
          background: #fef3c7;
          color: #92400e;
        }

        .client-query-modal-title {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
        }

        .client-query-modal-body {
          padding: 0 24px 24px 24px;
        }

        .client-query-modal-message {
          margin: 0;
          color: #6b7280;
          text-align: center;
          line-height: 1.5;
        }

        .client-query-modal-footer {
          padding: 16px 24px 24px 24px;
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .client-query-modal-btn {
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
          min-width: 80px;
        }

        .client-query-modal-btn-primary {
          background:#f43f5e;
          color: white;
        }

        .client-query-modal-btn-primary:hover {
          background: #2563eb;
        }

        .client-query-modal-btn-secondary {
          background: #f3f4f6;
          color: #374151;
        }

        .client-query-modal-btn-secondary:hover {
          background: #e5e7eb;
        }

        .client-query-modal-btn-danger {
          background: #ef4444;
          color: white;
        }

        .client-query-modal-btn-danger:hover {
          background: #dc2626;
        }

        .client-query-layout {
          display: flex;
          min-height: 100vh;
        }

        .client-query-image-side {
          width: 20%;
          background-image: url(https://team.survill.com/banner/store-banner.jpg);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-blend-mode: darken;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background-color: rgba(0, 0, 0, 0.6);

        }

        .client-query-image-content {
          flex: 1;
          padding: 40px;
          color: white;
        }

        .client-query-back-btn {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          margin: 20px;
          backdrop-filter: blur(10px);
        }

        .client-query-back-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
        }

        .client-query-form-side {
          width: 80%;
          display: flex;
          justify-content:center;
        }

        .client-query-container {
          width: 90%;
          padding: 40px 0;

        }

        .client-query-header {
          margin-bottom: 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }

        .client-query-header h1 {
          font-size: 32px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }

        .client-query-form {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .client-query-section {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .client-query-section h2 {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 20px 0;
        }

        .client-query-group {
          margin-bottom: 24px;
        }

        .client-query-group:last-child {
          margin-bottom: 0;
        }

        .client-query-group label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }

        .required {
          color: #ef4444;
          margin-left: 4px;
        }

        .client-query-input,
        .client-query-textarea,
        .client-query-select,
        .client-query-date,
        .client-query-time {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          transition: all 0.2s;
          font-family: inherit;
          box-sizing: border-box;
        }

        .client-query-input:focus,
        .client-query-textarea:focus,
        .client-query-select:focus,
        .client-query-date:focus,
        .client-query-time:focus {
          outline: none;
          border-color:#f43f5e;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .client-query-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .client-query-select-wrapper {
          position: relative;
        }

        .client-query-select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 12px center;
          background-repeat: no-repeat;
          background-size: 16px;
          padding-right: 40px;
        }

        .client-query-loading-text {
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
          display: block;
        }

        .client-query-radio-options {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .client-query-radio-option {
          position: relative;
          cursor: pointer;
          display: block;
        }

        .client-query-radio-option input[type="radio"] {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }

        .client-query-radio-content {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          transition: all 0.2s;
          background: white;
        }

        .client-query-radio-option input[type="radio"]:checked + .client-query-radio-content {
          border-color:#f43f5e;
          background: #f0f9ff;
        }

        .client-query-radio-check {
          width: 20px;
          height: 20px;
          border: 2px solid #d1d5db;
          border-radius: 50%;
          position: relative;
          transition: all 0.2s;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .client-query-radio-option input[type="radio"]:checked + .client-query-radio-content .client-query-radio-check {
          border-color:#f43f5e;
          background:#f43f5e;
        }

        .client-query-radio-option input[type="radio"]:checked + .client-query-radio-content .client-query-radio-check::after {
          content: '';
          position: absolute;
          top: 3px;
          left: 3px;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: white;
        }

        .client-query-radio-title {
          font-weight: 600;
          color: #1f2937;
          font-size: 14px;
          display: block;
          margin-bottom: 4px;
        }

        .client-query-radio-description {
          font-size: 13px;
          color: #6b7280;
          margin: 0;
          line-height: 1.4;
        }

        .client-query-status-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .client-query-status-pending {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
        }

        .client-query-status-in-progress {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid#f43f5e;
        }

        .client-query-status-completed {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #10b981;
        }

        .client-query-status-cancelled {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #ef4444;
        }

        .client-query-status-default {
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid #d1d5db;
        }

        .client-query-file-container {
          margin-bottom: 16px;
        }

        .client-query-file-upload {
          display: block;
          cursor: pointer;
        }

        .client-query-file-input {
          display: none;
        }

        .client-query-upload-content {
          border: 2px dashed #d1d5db;
          border-radius: 8px;
          padding: 24px;
          text-align: center;
          transition: all 0.2s;
          background: #fafbfc;
        }

        .client-query-upload-content:hover {
          border-color:#f43f5e;
          background: #f0f9ff;
        }

        .client-query-upload-icon {
          color: #6b7280;
          margin-bottom: 8px;
        }

        .client-query-upload-text span {
          display: block;
          font-weight: 600;
          color: #374151;
          margin-bottom: 4px;
        }

        .client-query-upload-text p {
          margin: 0;
          font-size: 13px;
          color: #6b7280;
        }

        .client-query-file-list {
          margin-top: 16px;
        }

        .client-query-file-list h3 {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 12px 0;
        }

        .client-query-file-list ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .client-query-file-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          margin-bottom: 8px;
          background: white;
        }

        .client-query-file-item:last-child {
          margin-bottom: 0;
        }

        .client-query-file-link {
          display: flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          color: #374151;
          flex: 1;
        }

        .client-query-file-icon {
          font-size: 16px;
        }

        .client-query-file-name {
          font-size: 13px;
          font-weight: 500;
        }

        .client-query-delete-file {
          background: #fee2e2;
          color: #991b1b;
          border: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          transition: all 0.2s;
        }

        .client-query-delete-file:hover {
          background: #fecaca;
        }

        .client-query-actions {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .client-query-right-actions {
          display: flex;
          gap: 12px;
        }

        .client-query-btn-primary,
        .client-query-btn-secondary,
        .client-query-btn-outline {
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }

        .client-query-btn-primary {
          background: #f43f5e;
          color: white;
        }

        .client-query-btn-primary:hover:not(:disabled) {
          background: #f72447ff;
          transform: translateY(-1px);
        }

        .client-query-btn-secondary {
          background: #f3f4f6;
          color: #374151;
        }

        .client-query-btn-secondary:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .client-query-btn-outline {
          background: white;
          color: #6b7280;
          border: 1px solid #d1d5db;
        }

        .client-query-btn-outline:hover:not(:disabled) {
          background: #f9fafb;
          border-color: #9ca3af;
        }

        .client-query-btn-primary:disabled,
        .client-query-btn-secondary:disabled,
        .client-query-btn-outline:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        @media (max-width: 968px) {
          .client-query-layout {
            flex-direction: column;
          }

          .client-query-image-side {
            width: 100%;
            height: auto;
            min-height: 120px;
          }

          .client-query-image-content {
            padding: 20px;
          }

          .client-query-back-btn {
            margin: 10px 20px 20px 20px;
          }

          .client-query-container {
            padding: 20px;
          }

          .client-query-header h1 {
            font-size: 24px;
          }

          .client-query-actions {
            flex-direction: column;
            gap: 12px;
          }

          .client-query-right-actions {
            width: 100%;
            justify-content: flex-end;
          }
        }
      `}</style>
    </div>
  );
}

export default CreateEditClientQueryForm;